<template>
  <v-container fluid class="broker-members">
    <div class="broker-members__header">
      <h1 class="broker-members__title">
        {{ $t("broker.members.brokerCommunityMembers") }}
      </h1>
      <p class="broker-members__subtitle">
        {{ $t("broker.members.manageMembersDesc") }}
      </p>
    </div>

    <!-- 2) Inputs row  -->
    <div class="broker-members__toolbar">
      <!-- Left side -->
      <div class="broker-members__toolbarLeft">
        <v-text-field
          v-model="search"
          class="broker-members__search"
          :placeholder="$t('broker.members.search')"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="fa-regular fa-magnifying-glass"
        />
        <v-btn
          class="broker-members__invitedBtn"
          outlined
          depressed
          @click="onSeeInvited"
          color="transparent"
        >
          <v-icon left size="18">fa-regular fa-user-clock</v-icon>
          {{ $t("broker.members.seeInvitedMembers") }}
        </v-btn>
      </div>
      <div class="broker-members__toolbarRight">
        <v-select
          v-model="selectedRole"
          class="broker-members__roleFilter"
          :items="roleOptions"
          item-text="text"
          item-value="value"
          :placeholder="$t('broker.members.filterByRole')"
          dense
          outlined
          hide-details
          clearable
        />

        <v-btn
          class="broker-members__addBtn"
          color="#20E3A2"
          depressed
          @click="onAddMember"
        >
          <v-icon left size="18">fa-regular fa-user-plus</v-icon>
          {{ $t("broker.members.addMember") }}
        </v-btn>
      </div>
    </div>

    <!-- 3) Table  -->
    <div class="container">
      <v-card outlined class="broker-members__tableCard">
        <v-simple-table class="broker-members__table">
          <thead class="broker-members__tableHeaderContainer">
            <tr class="broker-members__tableHeader">
              <th class="col col--profile">
                {{ $t("broker.members.profile") }}
              </th>
              <th class="col col--first">
                {{ $t("broker.members.firstName") }}
              </th>
              <th class="col col--last">{{ $t("broker.members.lastName") }}</th>
              <th class="col col--email">{{ $t("broker.members.email") }}</th>
              <th class="col col--role">{{ $t("broker.members.role") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="m in pagedMembers"
              :key="m.userId"
              class="broker-members__row"
            >
              <td class="col col--profile">
                <v-avatar size="34" class="broker-members__avatar">
                  <v-img v-if="m.imageUrl" :src="m.imageUrl" contain />
                  <img
                    v-else
                    :src="'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'"
                    class="profile-image"
                  />
                </v-avatar>
              </td>

              <td class="col col--first">{{ m.firstName }}</td>
              <td class="col col--last">{{ m.lastName }}</td>
              <td class="col col--email">{{ m.email }}</td>

              <td class="col col--role">
                <div
                  class="broker-members__roleChip"
                  :class="`broker-members__roleChip--${roleClass(m.role)}`"
                >
                  {{ roleLabel(m.role) }}
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Footer / pagination -->
        <div class="broker-members__tableFooter">
          <div class="broker-members__footerRight">
            <div class="broker-members__footerInfo">
              {{ rangeText }}
            </div>

            <div class="broker-members__footerControls">
              <v-pagination
                v-model="page"
                :length="totalPages"
                :total-visible="7"
                color="#20E3A2"
                class="broker-members__pagination"
                prev-icon="fa-solid fa-chevron-left"
                next-icon="fa-solid fa-chevron-right"
              />

              <v-select
                v-model="itemsPerPage"
                :items="[10, 20, 50]"
                dense
                outlined
                hide-details
                class="broker-members__perPage"
                menu-props="auto"
              />
            </div>
          </div>
        </div>
      </v-card>
    </div>
    <member-drawer
      v-model="addMemberDrawer"
      :role-options="roleOptions"
      @invite="handleInvite"
    />

    <invited-members-drawer
      v-model="invitedDrawer"
      :invites="invitedMembers"
      @resend="onResendInvite"
      @delete="onDeleteInvite"
    />

    <!-- Invite loading overlay -->
    <v-dialog
      v-model="inviteLoading"
      persistent
      max-width="320"
      content-class="broker-members__loadingDialog"
    >
      <v-card class="broker-members__loadingCard">
        <v-card-text class="text-center py-6">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            width="4"
            class="mb-3"
          />
          <p class="broker-members__loadingText">
            {{ $t("broker.members.inviteLoading") }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Invite result modal -->
    <v-dialog v-model="inviteResultDialog" max-width="480" persistent>
      <v-card class="broker-members__resultCard">
        <v-card-title class="broker-members__resultTitle">
          {{ $t("broker.members.inviteResultTitle") }}
        </v-card-title>
        <v-card-text class="broker-members__resultBody">
          <p
            v-if="inviteResult.createdStaff.length"
            class="broker-members__resultLine"
          >
            <strong>{{ inviteResult.createdStaff.length }}</strong>
            {{ $t("broker.members.inviteResultAdded") }}
          </p>
          <p
            v-if="inviteResult.invited.length"
            class="broker-members__resultLine"
          >
            <strong>{{ inviteResult.invited.length }}</strong>
            {{ $t("broker.members.inviteResultInvited") }}
          </p>
          <template v-if="inviteResult.errors.length">
            <p class="broker-members__resultLabel">
              {{ $t("broker.members.inviteResultAlreadyMembers") }}
            </p>
            <ul class="broker-members__resultErrorList">
              <li v-for="err in inviteResult.errors" :key="err.email">
                {{ err.email }}
                <span v-if="err.message" class="broker-members__resultErrorMsg"
                  >— {{ err.message }}</span
                >
              </li>
            </ul>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="inviteResultDialog = false">
            {{ $t("broker.members.inviteResultClose") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import membersService from "../services/members.service";
import MemberDrawer from "../components/MemberDrawer.vue";
import InvitedMembersDrawer from "../components/InvitedMembersDrawer.vue";

export default {
  name: "brokerMembers",
  components: { MemberDrawer, InvitedMembersDrawer },
  data() {
    return {
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: "",
      search: "",
      selectedRole: null,
      roleOptions: [
        { text: "Admin", value: "ADMIN" },
        { text: "Read-Only", value: "READ_ONLY" },
      ],
      members: [],
      page: 1,
      itemsPerPage: 10,
      addMemberDrawer: false,
      invitedDrawer: false,

      invitedMembers: [],

      inviteLoading: false,
      inviteResultDialog: false,
      inviteResult: {
        createdStaff: [],
        invited: [],
        errors: [],
      },
    };
  },

  async mounted() {
    await this.loadMembers();
  },

  computed: {
    filteredMembers() {
      const s = (this.search || "").toLowerCase().trim();

      return this.members
        .filter((m) => !this.selectedRole || m.role === this.selectedRole)
        .filter((m) => {
          if (!s) return true;
          return (
            (m.firstName || "").toLowerCase().includes(s) ||
            (m.lastName || "").toLowerCase().includes(s) ||
            (m.email || "").toLowerCase().includes(s)
          );
        });
    },

    totalPages() {
      const total = this.filteredMembers.length;
      return Math.max(1, Math.ceil(total / this.itemsPerPage));
    },
    pagedMembers() {
      // si cambia el filtro y la página queda fuera del rango, la ajustamos
      if (this.page > this.totalPages) this.page = this.totalPages;

      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredMembers.slice(start, start + this.itemsPerPage);
    },

    rangeText() {
      const total = this.filteredMembers.length;
      if (total === 0) return "0-0 of 0";

      const start = (this.page - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.page * this.itemsPerPage, total);
      return `${start}-${end} of ${total}`;
    },
  },

  watch: {
    // Si cambian filtros o búsqueda, volvemos a la primera página
    search() {
      this.page = 1;
    },
    selectedRole() {
      this.page = 1;
    },
    itemsPerPage() {
      this.page = 1;
    },
  },

  methods: {
    async onSeeInvited() {
      this.addMemberDrawer = false;
      this.invitedDrawer = true;
      await this.loadInvitedMembers();
    },
    async loadInvitedMembers() {
      try {
        const data = await membersService.getStaffInvited();
        this.invitedMembers = (Array.isArray(data) ? data : []).map((item) => ({
          id: item.email,
          email: item.email,
          role: item.role,
          lastSent: item.lastSent,
        }));
      } catch (error) {
        console.error("Error loading invited members:", error);
        this.invitedMembers = [];
      }
    },
    onAddMember() {
      console.log("Add member");
      this.addMemberDrawer = true;
    },
    async handleInvite(rows) {
      this.addMemberDrawer = false;
      this.inviteLoading = true;
      try {
        const result = await membersService.inviteMembers(rows);
        if (result.createdStaff?.length) {
          const map = new Map(this.members.map((m) => [m.email, m]));
          result.createdStaff.forEach((m) => map.set(m.email, m));
          this.members = Array.from(map.values());
        }
        await this.loadInvitedMembers();
        this.inviteResult = {
          createdStaff: result.createdStaff ?? [],
          invited: result.invited ?? [],
          errors: result.errors ?? [],
        };
        this.inviteResultDialog = true;
      } catch (error) {
        console.error("Error inviting members:", error);
      } finally {
        this.inviteLoading = false;
      }
    },
    async loadMembers() {
      try {
        this.isLoading = true;
        this.loadingMessage =
          this.$t("broker.members.loadingMembers") || "Loading members...";

        const data = await membersService.getStaffMembers();
        this.members = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading members:", error);
        this.members = [];
      } finally {
        this.isLoading = false;
        this.isInitialLoading = false;
      }
    },

    async onResendInvite(item, cb) {
      if (!item?.email) return;
      try {
        await membersService.resendStaffInvite(item.email);
        await this.loadInvitedMembers();
        cb?.resolve();
      } catch (error) {
        console.error("Error resending invite:", error);
        cb?.reject(error);
      }
    },

    async onDeleteInvite(item, cb) {
      if (!item?.email) {
        cb?.reject(new Error("Missing email"));
        return;
      }
      try {
        await membersService.deleteInvited(item.email);
        await this.loadInvitedMembers();
        cb?.resolve();
      } catch (error) {
        console.error("Error resending invite:", error);
        cb?.reject(error);
      }
    },

    // Tabla helpers
    roleLabel(role) {
      return role === "ADMIN" ? "Admin" : "Read-Only";
    },
    roleClass(role) {
      return role === "ADMIN" ? "admin" : "readonly";
    },

    prevPage() {
      this.page = Math.max(1, this.page - 1);
    },
    nextPage() {
      this.page = Math.min(this.totalPages, this.page + 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.broker-members {
  border-radius: 12px;
  overflow: hidden;
  &__title {
    margin-bottom: 20px;
  }

  &__subtitle {
    margin-bottom: 20px;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin: 0px 5px 30px;

  }

  &__toolbarLeft,
  &__toolbarRight {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  &__search {
    width: 230px;
  }

  &__roleFilter {
    width: 180px;
  }

  &__invitedBtn {
    text-transform: none;
    border-radius: 10px;
    background-color: #ebebeb !important;
  }

  &__addBtn {
    text-transform: none;
    border-radius: 10px;
    color: #0f2b23; /* para que se parezca a la captura */
    font-weight: 600;
  }
  //table
  &__tableHeaderContainer {
    background-color: #20e3a2 !important;
    color: red;
  }
  &__tableHeader {
    background: #20e3a2;
    align-items: center;
    padding: 12px 16px;
    font-weight: 700;
    font-size: 16px !important;
    color: #0f2b23;
  }
  &__table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    th,
    td {
      padding: 14px 16px;
      font-size: 13px;
      text-align: left;
      vertical-align: middle;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    thead tr {
      background: #20e3a2;
      color: #0f2b23;
      font-weight: 700;
    }

    tbody tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      &:hover {
        background: rgba(32, 227, 162, 0.06);
      }
    }

    .col--profile {
      width: 80px;
    }
    .col--first {
      width: auto;
    }
    .col--last {
      width: auto;
    }
    .col--email {
      width: auto;
    }
    .col--role {
      width: 150px;
    }
  }
  &__avatar {
    background: rgba(0, 0, 0, 0.06);
  }
  &__row:hover {
    background: rgba(32, 227, 162, 0.06);
  }

  &__roleChip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;

    &--admin {
    background: #e0f3fe;
    color: #045684 !important;
    }

    &--readonly {
    background: #d7ffed;
    color: #005931 !important;
    }
  }
  // Table footer

  &__tableFooter {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
  }

  &__footerRight {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  &__footerInfo {
    font-size: 12px;
    margin-left: auto;
    opacity: 0.6;
  }

  &__footerControls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__pagination {
    ::v-deep .v-pagination__item,
    ::v-deep .v-pagination__navigation {
      border-radius: 999px;
      min-width: 24px;
      height: 24px;
      box-shadow: none;
      padding: 0px;
      ::v-deep .v-icon.v-icon {
        font-size: 16px;
      }
    }
  }

  &__perPage {
    width: 84px;
  }

  &__loadingDialog {
    .v-dialog {
      border-radius: 12px;
    }
  }

  &__loadingCard {
    border-radius: 12px;
  }

  &__loadingText {
    margin: 0;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
  }

  &__resultCard {
    border-radius: 12px;
  }

  &__resultTitle {
    font-size: 18px;
    font-weight: 700;
  }

  &__resultBody {
    padding-top: 0;
  }

  &__resultLine {
    margin: 8px 0;
    font-size: 14px;
  }

  &__resultLabel {
    margin: 12px 0 6px;
    font-size: 13px;
    font-weight: 600;
    opacity: 0.9;
  }

  &__resultErrorList {
    margin: 0;
    padding-left: 20px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
  }

  &__resultErrorMsg {
    font-size: 12px;
    opacity: 0.8;
  }
}

.broker-app
  .v-application
  .broker-members__invitedBtn.v-btn:not(.v-btn--disabled) {
  background-color: #ebebeb !important;
  color: #14161b !important;
}

.broker-app .v-application .broker-members__addBtn.v-btn:not(.v-btn--disabled) {
  color: #3b3b3b !important;
}

.broker-app .v-data-table .broker-members__tableHeader thead th {
  background-color: #20e3a2 !important;
}

#broker-app .container .v-data-table thead th {
  background-color: #28ea96 !important;
}
.broker-members__table tbody td[data-v-b1597086] {
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}

.container{
  padding: 0px !important;
}


::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th,
::v-deep .v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
  font-size: 16px !important;
}
</style>
